import type {
  BlacklistCarriersResponse,
  CarrierBookingsListResponse,
  CarrierComplianceResponse,
  CarrierDetailResponse,
  CarrierDriversListResponse,
  CarrierEquipmentListResponse,
} from "api-client";
import {
  CARRIER_BOOKINGS_SORT_ARRIVAL_UI,
  mapApiCarrierBookingRowToApiBooking,
  rejectMessage,
} from "api-client";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { mapApiBookingToBookingRow, type BookingRow } from "design-components";
import { OPERATOR_SCOPED_ID } from "../../constants/operator.constants";
import { carriersService } from "../../services/carriers.service";
import type {
  CarrierBookingsListState,
  CarrierDetailState,
  CarrierDriversListState,
} from "./carrier-detail.models";

type CarrierDetailRootSlice = { carrierDetail: CarrierDetailState };

function resolveDriverListActiveOnly(d: CarrierDriversListState): { activeOnly?: boolean } {
  if (d.showOnlyActive) {
    return { activeOnly: true };
  }
  return {};
}

export const fetchCarrierDetail = createAsyncThunk<
  CarrierDetailResponse,
  { carrierId: string },
  { state: CarrierDetailRootSlice; rejectValue: string }
>("carrierDetail/fetch", async ({ carrierId }, { rejectWithValue }) => {
  try {
    return await carriersService.getCarrierOverview(carrierId, OPERATOR_SCOPED_ID);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch carrier overview"));
  }
});

export const setCarrierYardBlacklist = createAsyncThunk<
  BlacklistCarriersResponse,
  { carrierId: number; action: "blacklist" | "unblacklist" },
  { state: CarrierDetailRootSlice; rejectValue: string }
>("carrierDetail/setYardBlacklist", async ({ carrierId, action }, { dispatch, rejectWithValue }) => {
  if (!Number.isFinite(carrierId) || carrierId <= 0) {
    return rejectWithValue("Invalid carrier");
  }
  try {
    const res = await carriersService.blacklistCarriers([OPERATOR_SCOPED_ID], {
      carrierIds: [carrierId],
      action,
    });
    const id = String(carrierId);
    try {
      await dispatch(fetchCarrierDetail({ carrierId: id })).unwrap();
    } catch (e: unknown) {
      return rejectWithValue(rejectMessage(e, "Carrier updated but overview refresh failed"));
    }
    return res;
  } catch (e: unknown) {
    return rejectWithValue(
      rejectMessage(e, action === "unblacklist" ? "Failed to unblacklist from yard" : "Failed to blacklist carrier"),
    );
  }
});

export const fetchCarrierDrivers = createAsyncThunk<
  CarrierDriversListResponse,
  void,
  { state: CarrierDetailRootSlice; rejectValue: string }
>("carrierDetail/fetchDrivers", async (_, { getState, rejectWithValue }) => {
  const { selectedCarrierId, driversList } = getState().carrierDetail;
  if (!selectedCarrierId) {
    return rejectWithValue("No carrier selected");
  }
  try {
    return await carriersService.getCarrierDrivers(selectedCarrierId, {
      fromIndex: driversList.fromIndex,
      toIndex: driversList.toIndex,
      sortingField: driversList.sortField,
      sortingOrder: driversList.sortOrder,
      search: driversList.search,
      operatorId: OPERATOR_SCOPED_ID,
      ...resolveDriverListActiveOnly(driversList),
    });
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch drivers"));
  }
});

export const fetchCarrierCompliance = createAsyncThunk<
  CarrierComplianceResponse,
  void,
  { state: CarrierDetailRootSlice; rejectValue: string }
>("carrierDetail/fetchCompliance", async (_, { getState, rejectWithValue }) => {
  const { selectedCarrierId } = getState().carrierDetail;
  if (!selectedCarrierId) {
    return rejectWithValue("No carrier selected");
  }
  try {
    return await carriersService.getCarrierCompliance(selectedCarrierId);
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch compliance"));
  }
});

export const fetchCarrierEquipment = createAsyncThunk<
  CarrierEquipmentListResponse,
  { carrierId: string },
  { state: CarrierDetailRootSlice; rejectValue: string }
>("carrierDetail/fetchEquipment", async ({ carrierId }, { getState, rejectWithValue }) => {
  const { equipmentList } = getState().carrierDetail;
  try {
    return await carriersService.getCarrierEquipment(carrierId, {
      fromIndex: equipmentList.fromIndex,
      toIndex: equipmentList.toIndex,
      search: equipmentList.search,
      operatorId: OPERATOR_SCOPED_ID,
    });
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch equipment"));
  }
});

export interface CarrierBookingsFetchPayload {
  rows: BookingRow[];
  totalCount: number;
}

function resolveCarrierBookingsDateRange(
  list: CarrierBookingsListState,
): { dateFrom: string | null; dateTo: string | null } {
  const df = list.dateFrom?.trim() || null;
  const dt = list.dateTo?.trim() || null;
  if (df && !dt) return { dateFrom: df, dateTo: df };
  return { dateFrom: df, dateTo: dt };
}

export const fetchCarrierBookings = createAsyncThunk<
  CarrierBookingsFetchPayload,
  { carrierId: string },
  { state: CarrierDetailRootSlice; rejectValue: string }
>("carrierDetail/fetchBookings", async ({ carrierId }, { getState, rejectWithValue }) => {
  const { selectedCarrierId, bookingsList } = getState().carrierDetail;
  const resolvedCarrierId = carrierId.trim() || selectedCarrierId;
  if (!resolvedCarrierId) {
    return rejectWithValue("No carrier selected");
  }
  const { dateFrom, dateTo } = resolveCarrierBookingsDateRange(bookingsList);
  const statusValues =
    bookingsList.statusFilterValues.map((s) => s.trim().toUpperCase()).filter(Boolean);
  try {
    const res: CarrierBookingsListResponse = await carriersService.getCarrierBookings(
      resolvedCarrierId,
      {
        operatorId: OPERATOR_SCOPED_ID,
        searchByName: bookingsList.search.trim(),
        dateFrom,
        dateTo,
        statusValues: statusValues.length > 0 ? statusValues : undefined,
        fromIndex: bookingsList.fromIndex,
        toIndex: bookingsList.toIndex,
        sortingField: bookingsList.sortingField || CARRIER_BOOKINGS_SORT_ARRIVAL_UI,
        sortingOrder: bookingsList.sortingOrder,
      },
    );
    const rows = res.data.bookings.map((row) =>
      mapApiBookingToBookingRow(mapApiCarrierBookingRowToApiBooking(row)),
    );
    return { rows, totalCount: res.data.totalCount };
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to fetch bookings"));
  }
});
